import { hashWords } from "daedalus-core";
import { GAME_EPOCH_MS, TURN_MS } from "../time/game-clock.ts";
import { MAX_CLOUD_SLOTS, WIND_EDGE_FADE_MS, type CloudShadowPreset, type WindSpritePreset } from "./wind-presets.ts";
import type { Wind } from "./wind.ts";

/**
 * Where every wisp, dash and cloud shadow is, as a pure function of the clock and the world seed (ADR-144's amendment of
 * 2026-10-31). Nothing is queued, spawned or released: an instance exists and moves as `x = start + (now - start) * speed`, its
 * start, lane, variant and size drawn from the shared seeded hash, so every page agrees, a page that sleeps an hour and wakes draws
 * only what crosses now, and a position computed at any time equals the one reached by stepping frame by frame. The functions
 * write into slots the caller owns, so a frame allocates nothing. Never `Math.random`.
 */

/**
 * A wisp or dash to draw: which variant of its preset, the frame of that variant's sheet, where its box's top left is in
 * pixels from the view's top left, its opacity and whether the sheet is flipped.
 */
export interface WindSpriteSlot {
  variant: number;
  frame: number;
  x: number;
  y: number;
  alpha: number;
  flipX: boolean;
}

/**
 * A cloud shadow to draw: its silhouette, where its box's top left is in world pixels, its scale, whether it is mirrored and
 * how far its turn has faded it in, 0 to 1.
 */
export interface CloudSlot {
  shape: number;
  x: number;
  y: number;
  scale: number;
  flipX: boolean;
  fade: number;
}

/**
 * The part of a view the schedule reads: its size, and for clouds its place in the world.
 */
export interface ScheduleView {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * The clouds of one turn: how many a tile holds, how fast they drift and which way. The turn is what a field is a function of,
 * so a change of wind or weather at a turn's edge never moves a cloud.
 */
export interface CloudField {
  readonly turn: number;
  readonly count: number;
  readonly speedPxPerS: number;
  readonly heading: Wind["heading"];
}

const WORD_COUNT = 5;
const SALT_X = 1;
const SALT_Y = 2;
const SALT_VARIANT = 3;
const SALT_SPEED = 4;
const SALT_SCALE = 5;
const SALT_MIRROR = 6;
const UNIT = 1 / 0x1000000;
const DRAW_SHIFT = 8;
const START_BEFORE = 0.5;
const START_WITHIN = 0.9;
const scratch = new Uint32Array(WORD_COUNT);

/**
 * A uniform draw from 0 to 1 for one decision of one instance.
 *
 * @param seed The world's seed.
 * @param region The region's word (`regionWord`).
 * @param domain The preset's domain.
 * @param a The instance (and, for cloud, the turn) the draw is for.
 * @param b The cycle of the instance, or its index.
 * @param salt Which decision.
 * @returns A number from 0 up to but not including 1.
 */
function draw(seed: number, region: number, domain: number, a: number, b: number, salt: number): number {
  scratch[0] = region;
  scratch[1] = domain;
  scratch[2] = a;
  scratch[3] = b;
  scratch[4] = salt;
  return (hashWords(seed, scratch, WORD_COUNT) >>> DRAW_SHIFT) * UNIT;
}

/**
 * Makes slots for a pool of wisps or dashes.
 *
 * @param capacity How many.
 * @returns The slots, all zero.
 */
export function makeWindSlots(capacity: number): WindSpriteSlot[] {
  return Array.from({ length: capacity }, () => ({ variant: 0, frame: 0, x: 0, y: 0, alpha: 0, flipX: false }));
}

/**
 * Makes slots for the cloud shadows of one view.
 *
 * @returns `MAX_CLOUD_SLOTS` slots, all zero.
 */
export function makeCloudSlots(): CloudSlot[] {
  return Array.from({ length: MAX_CLOUD_SLOTS }, () => ({ shape: 0, x: 0, y: 0, scale: 1, flipX: false, fade: 0 }));
}

/**
 * How many instances of a preset run in a wind: its count for the strength, halved (rounded up) at Reduced.
 *
 * @param preset The preset.
 * @param wind The wind.
 * @param reduced Whether Effects is Reduced.
 * @returns The count.
 */
export function windCount(preset: WindSpritePreset, wind: Wind, reduced: boolean): number {
  const count = preset.count[wind.strength];
  return reduced ? Math.ceil(count / 2) : count;
}

/**
 * How far the turn's edge has faded the wind: 0 at the turn's start and end, rising to 1 over `WIND_EDGE_FADE_MS` and falling
 * over the last `WIND_EDGE_FADE_MS`.
 *
 * @param epochMs The clock in milliseconds since the Unix epoch.
 * @returns 0 to 1.
 */
export function windEdgeFade(epochMs: number): number {
  const into = (((epochMs - GAME_EPOCH_MS) % TURN_MS) + TURN_MS) % TURN_MS;
  const edge = Math.min(into, TURN_MS - into) / WIND_EDGE_FADE_MS;
  return edge <= 0 ? 0 : edge >= 1 ? 1 : edge;
}

/**
 * The wisps or dashes on screen now, written into the caller's slots.
 *
 * Instance `i` of `n` repeats every `periodMs`, staggered by `periodMs / n`. In each cycle it draws a variant, a start in the
 * view and a height in the lane, lives for its variant's frames at `frameMs` each, and drifts along the heading; it is drawn only
 * while it is alive and any of it is inside the view. The sheet is flipped for the heading the preset names. Its opacity is
 * scaled by `windEdgeFade`, so nothing is drawn at full strength across a turn's edge, where the wind may change.
 *
 * @param preset A wisp or dash preset.
 * @param wind The wind of the turn.
 * @param reduced Whether Effects is Reduced (halves the count).
 * @param worldSeed The world's seed.
 * @param region The region's word (`regionWord`).
 * @param epochMs The clock in milliseconds since the Unix epoch.
 * @param viewWidth The view's width in world pixels.
 * @param viewHeight The view's height in world pixels.
 * @param out Slots to write; as many as the preset's largest count.
 * @returns How many slots were written, from the first.
 */
export function windSpritesInto(preset: WindSpritePreset, wind: Wind, reduced: boolean, worldSeed: number, region: number, epochMs: number, viewWidth: number, viewHeight: number, out: WindSpriteSlot[]): number {
  const count = windCount(preset, wind, reduced);
  if (count === 0 || preset.frameMs[wind.strength] <= 0) return 0;
  const hold = preset.frameMs[wind.strength] as number;
  const drift = preset.driftPxPerS[wind.strength] as number;
  const edge = windEdgeFade(epochMs);
  if (edge <= 0) return 0;
  const alpha = (preset.opacity[wind.strength] as number) * edge;
  const period = preset.periodMs;
  const flipX = wind.heading === preset.flipWithHeading;
  const lane = preset.lane;
  let written = 0;
  for (let i = 0; i < count && written < out.length; i += 1) {
    const time = epochMs + (i * period) / count;
    const cycle = Math.floor(time / period);
    const age = time - cycle * period;
    const id = cycle >>> 0;
    const variantIndex = Math.floor(draw(worldSeed, region, preset.domain, i, id, SALT_VARIANT) * preset.variants.length);
    const variant = preset.variants[variantIndex] as (typeof preset.variants)[number];
    if (age >= variant.frames * hold) continue;
    const start = -variant.frameWidth * START_BEFORE + draw(worldSeed, region, preset.domain, i, id, SALT_X) * (viewWidth * START_WITHIN + variant.frameWidth * START_BEFORE);
    const along = start + (drift * age) / 1000;
    const x = wind.heading === "right" ? along : viewWidth - along - variant.frameWidth;
    if (x + variant.frameWidth <= 0 || x >= viewWidth) continue;
    const centre = (lane[0] + draw(worldSeed, region, preset.domain, i, id, SALT_Y) * (lane[1] - lane[0])) * viewHeight;
    const slot = out[written] as WindSpriteSlot;
    slot.variant = variantIndex;
    slot.frame = variant.first + Math.min(variant.frames - 1, Math.floor(age / hold));
    slot.x = x;
    slot.y = centre - variant.frameHeight / 2;
    slot.alpha = alpha;
    slot.flipX = flipX;
    written += 1;
  }
  return written;
}

/**
 * The clouds of a turn.
 *
 * @param preset The cloud preset.
 * @param turn The turn.
 * @param wind The wind of that turn.
 * @param count How many clouds a tile holds that turn (`cloudCountFor`, halved at Reduced by the caller).
 * @returns The field.
 */
export function cloudFieldOf(preset: CloudShadowPreset, turn: number, wind: Wind, count: number): CloudField {
  return { turn, count, speedPxPerS: preset.speedPxPerS[wind.strength] as number, heading: wind.heading };
}

/**
 * How far a turn has faded its clouds in or out: 0 at the turn's edges, rising to 1 over `fadeMs` and falling over the last
 * `fadeMs`.
 *
 * @param preset The cloud preset.
 * @param msIntoTurn Milliseconds since the turn began.
 * @returns 0 to 1.
 */
export function cloudFade(preset: CloudShadowPreset, msIntoTurn: number): number {
  const edge = Math.min(msIntoTurn, TURN_MS - msIntoTurn) / preset.fadeMs;
  return edge <= 0 ? 0 : edge >= 1 ? 1 : edge;
}

/**
 * The cloud shadows crossing a view now, written into the caller's slots.
 *
 * The clouds of a turn are a field that repeats every `tile` world pixels. Cloud `i` starts at a hashed place in the tile and
 * drifts along the heading at the field's speed times a hashed share, from the start of the turn, so `x = start + elapsed * speed`.
 * A cloud is written once for each repeat of the tile that puts any of it in the view; nothing off screen is written.
 *
 * @param preset The cloud preset.
 * @param field The turn's field (`cloudFieldOf`).
 * @param worldSeed The world's seed.
 * @param region The region's word (`regionWord`).
 * @param epochMs The clock in milliseconds since the Unix epoch.
 * @param view The view in world pixels.
 * @param out Slots to write (`makeCloudSlots`).
 * @returns How many slots were written, from the first.
 */
export function cloudShadowsInto(preset: CloudShadowPreset, field: CloudField, worldSeed: number, region: number, epochMs: number, view: ScheduleView, out: CloudSlot[]): number {
  const elapsed = epochMs - GAME_EPOCH_MS - field.turn * TURN_MS;
  const fade = cloudFade(preset, elapsed);
  if (field.count <= 0 || fade <= 0) return 0;
  const tileWidth = preset.tile.width;
  const tileHeight = preset.tile.height;
  const direction = field.heading === "right" ? 1 : -1;
  const turn = field.turn >>> 0;
  let written = 0;
  for (let i = 0; i < field.count && written < out.length; i += 1) {
    const scale = preset.scales[Math.floor(draw(worldSeed, region, preset.domain, turn, i, SALT_SCALE) * preset.scales.length)] as number;
    const width = preset.frameWidth * scale;
    const height = preset.frameHeight * scale;
    const spread = preset.speedSpread[0] + draw(worldSeed, region, preset.domain, turn, i, SALT_SPEED) * (preset.speedSpread[1] - preset.speedSpread[0]);
    const moved = direction * field.speedPxPerS * spread * (elapsed / 1000);
    const baseX = positive(draw(worldSeed, region, preset.domain, turn, i, SALT_X) * tileWidth + moved, tileWidth);
    const baseY = draw(worldSeed, region, preset.domain, turn, i, SALT_Y) * tileHeight;
    const shape = Math.floor(draw(worldSeed, region, preset.domain, turn, i, SALT_VARIANT) * preset.shapes);
    const flipX = draw(worldSeed, region, preset.domain, turn, i, SALT_MIRROR) < 0.5;
    const firstX = Math.floor((view.x - width - baseX) / tileWidth) + 1;
    const lastX = Math.ceil((view.x + view.width - baseX) / tileWidth) - 1;
    const firstY = Math.floor((view.y - height - baseY) / tileHeight) + 1;
    const lastY = Math.ceil((view.y + view.height - baseY) / tileHeight) - 1;
    for (let ny = firstY; ny <= lastY; ny += 1) {
      for (let nx = firstX; nx <= lastX; nx += 1) {
        if (written >= out.length) return written;
        const slot = out[written] as CloudSlot;
        slot.shape = shape;
        slot.x = baseX + nx * tileWidth;
        slot.y = baseY + ny * tileHeight;
        slot.scale = scale;
        slot.flipX = flipX;
        slot.fade = fade;
        written += 1;
      }
    }
  }
  return written;
}

/**
 * Wraps a position into `[0, span)`.
 *
 * @param value A position, possibly negative or past the span.
 * @param span The span.
 * @returns The position inside the span.
 */
function positive(value: number, span: number): number {
  const wrapped = value % span;
  return wrapped < 0 ? wrapped + span : wrapped;
}
